import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
// Material
import { Autocomplete, Box, ListItem, Paper, Skeleton, TextField } from "@mui/material";
// Icons
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
// Components
import InputFormExtension from "@/Layouts/components/InputComponents/InputFormExtension";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import ChipWithTooltip from "@Layouts/components/Chip/ChipWithTooltip";
import { CustomPopper } from "@/Layouts/components/CustomPopper";
// Helpers
import { maxHeight } from "@/Common/helpers/visuals";
// Hooks
import { useIsProducer } from "@/Common/hooks/Positions";
// Constants
import { modalities } from "@/Constants/ptoModalities";

export default function SelectMultiplePto(props) {
	const {
		id = "",
		options: defaultOptions = [],
		// value: defaultValue = [],
		initialValue = [],
		className = "",
		items = 7,
		information = {},
		sx = {},
		disabled = false,
		disableClearable = false,
		loading = false,
		multiple = true,
		onClear = () => {},
		onSelectOption = () => {},
		onRemoveOption = () => {},
		onSettled = () => {},
	} = props;

	const [value, setValue] = useState([]);
	const [fixedValue, setFixedValue] = useState("");
	const [excludeHolidays, setExcludeHolidays] = useState(false);
	const [open, setOpen] = useState(false);

	const isProducer = useIsProducer(information?.position);

	const showExcludeHoliday = useMemo(
		() =>
			(value?.[1]?.label === "Last xQs" && value.length === 3) ||
			(value?.[1]?.label === "Working days proration" && value.length === 2),

		[value]
	);

	const options = useMemo(() => {
		switch (value?.length) {
			case 0:
				return defaultOptions;
			case 1:
				return modalities
					?.filter((item) => {
						let response = true;
						if (item.label === "Draw Equivalent") {
							response = information.drawModality === "Flat (Draw)";
						} else if (item.label === "Working days proration") {
							response = !isProducer;
						}
						return response;
					})
					?.map((item) => ({
						label: item.label,
						hasOptions: !!item?.options?.length,
					}));

			case 2:
				return (
					modalities
						?.find((item) => item.label === value[1]?.label)
						?.options?.map((item) => ({
							label: item.label,
							hasOptions: !!item?.options?.length,
						})) || []
				);
			default:
				return [];
		}
	}, [isProducer, information?.drawModality, defaultOptions, value]);

	const eventsEmitter = (event, newValue, reason) => {
		event.preventDefault();
		event.stopPropagation();
		switch (reason) {
			case "selectOption":
				if (!value.includes(newValue.label)) {
					setValue([...value, newValue]);
					onSelectOption?.(newValue);
				}
				break;
			case "removeOption":
				setValue(newValue);
				if (fixedValue) {
					setFixedValue("");
				}

				if (value.length === 1) {
					onClear?.(value[0]);
				} else {
					setOpen(true);
					onRemoveOption?.(newValue);
				}
				break;
			case "clear":
				setValue([]);
				onClear?.(value[0]);
				break;
			case "settled":
				if (!value.includes(newValue.label) && value?.[1]?.label !== "Flat") {
					setValue([...value, newValue]);
					onSettled?.([...value, newValue]);
				} else if (!value.includes(newValue.label) && value?.[1]?.label === "Flat") {
					setValue([...value, newValue]);
				}
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		if (initialValue?.length) {
			const arrSorted = initialValue.sort((a, b) => a.order - b.order);
			switch (arrSorted?.[1]?.label) {
				case "Last xQs":
					setValue(arrSorted.slice(0, 3));
					setExcludeHolidays(arrSorted?.[3].label);
					break;
				case "Flat":
					setValue(arrSorted.slice(0, 3));
					setFixedValue(arrSorted?.[4]?.label?.toString());
					break;
				case "Working days proration":
					setExcludeHolidays(arrSorted?.[2].label);
					setValue(arrSorted.slice(0, 2));
					break;
				default:
					setValue(arrSorted);
					break;
			}
		} else {
			setValue([]);
		}

		return () => {
			setFixedValue("");
		};
	}, [initialValue]);

	return (
		<Paper elevation={0} sx={{ width: "100%", backgroundColor: "transparent" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					columnGap: "0.3rem",
				}}
			>
				{loading ? (
					<Skeleton
						height={31.84}
						width={250}
						variant="rectangular"
						sx={{
							borderRadius: "4px",
						}}
					/>
				) : (
					<>
						<div>
							<Autocomplete
								id={id}
								disabled={disabled}
								multiple
								disableCloseOnSelect
								disablePortal
								open={open && !!options?.length}
								onOpen={() => {
									setOpen(true);
								}}
								onClose={() => {
									setOpen(false);
								}}
								value={value}
								onChange={eventsEmitter}
								options={options}
								sx={{
									...sx,
									minWidth: "100%",
									"& .MuiInputBase-root": {
										padding: "2px 30px 1px 2px !important",
									},
									"& .MuiAutocomplete-tag": {
										margin: "-1px 0px 0px 1px",
									},
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										component="li"
										type={"button"}
										className={clsx({
											[className]: true,
											"advance-select-open": open,
											"advance-select-expand-area": disableClearable,
										})}
									/>
								)}
								getOptionLabel={(option) => option.label}
								forcePopupIcon={!!options?.length}
								isOptionEqualToValue={() => true}
								renderTags={(value, getTagProps) => {
									return value.map((option, index) => (
										<ChipWithTooltip
											key={index}
											index={index}
											option={option}
											value={value}
											getTagProps={getTagProps}
											disabled={disabled}
										/>
									));
								}}
								renderOption={(props, option) => {
									const eventMessage = option.hasOptions || !value?.length ? "selectOption" : "settled";
									return (
										<ListItem
											{...props}
											sx={{
												backgroundColor: "white !important",
												display: "flex !important",
												alignItems: "center !important",
												justifyContent: "space-between !important",
												"&:hover": {
													backgroundColor: "var(--gray-background) !important",
												},
											}}
											onClick={(event) => eventsEmitter(event, option, eventMessage)}
										>
											<span>{option.label.toUpperCase()}</span>
											{!value.length || option.hasOptions ? (
												<ArrowForwardIosIcon
													sx={{
														fontSize: "12px",
														color: "var(--text-color)",
													}}
												/>
											) : null}
										</ListItem>
									);
								}}
								PopperComponent={CustomPopper}
								popupIcon={<ArrowForwardIosRoundedIcon size="small" sx={{ padding: "4px", transform: "rotate(90deg)" }} />}
								ListboxProps={{
									className: "advanced-select-dropdown",
									sx: { maxHeight: maxHeight(items) },
								}}
							/>
						</div>
						<div>
							{value?.[1]?.label === "Flat" && value.length === 3 && (
								<InputFormExtension
									readOnly={disabled}
									data={[
										{
											type: "Dollar",
											max: 9999,
											min: 0,
											decimals: 2,
										},
									]}
									sx={{
										width: "100px",
									}}
									value={fixedValue}
									onActionUpdate={(inputValue) => {
										setFixedValue(inputValue);
										onSettled?.([...value, { flatValue: inputValue, input: "Dollar" }]);
									}}
								/>
							)}

							{showExcludeHoliday && (
								<CustomizedSwitches
									sx={{
										position: "relative",
										bottom: "2px",
									}}
									disabled={disabled}
									textChecked={"Exclude Holidays"}
									textUnChecked={"Exclude Holidays"}
									checked={excludeHolidays}
									onChange={() => {
										setExcludeHolidays((prev) => !prev);
										onSettled?.([...value, { excludeHolidays: !excludeHolidays }]);
									}}
								/>
							)}
						</div>
					</>
				)}
			</Box>
		</Paper>
	);
}
SelectMultiplePto.propTypes = {
	id: PropTypes.string,
	className: PropTypes.string,
	items: PropTypes.number,
	sx: PropTypes.object,
	information: PropTypes.object,
	options: PropTypes.array,
	value: PropTypes.array,
	initialValue: PropTypes.array,
	disabled: PropTypes.bool,
	disableClearable: PropTypes.bool,
	loading: PropTypes.bool,
	multiple: PropTypes.bool,
	onClear: PropTypes.func,
	onSelectOption: PropTypes.func,
	onRemoveOption: PropTypes.func,
	onSettled: PropTypes.func,
};
